import React from 'react';

import { Col, DatePicker, Input, Row, Select } from 'antd';
import moment from 'moment';
import { useIntl } from 'react-intl';

import type {
  FilterValues,
  SetValueProps,
} from '@src/components/FilterDropDown/FilterDropDown.interfaces';
import { ABSENCE_TYPES } from '@src/containers/App/TimeTracker/Main/components/reports/shared/constants';
import {
  ABSENCES_LIST_FILTER_FIELDS,
  buildAbsencesListFilterSetValueProps,
} from '@src/packages/absences/absencesListFilterConfig';
import { DateUtil } from '@src/packages/utils/dates';

const { RangePicker } = DatePicker;
const { Option } = Select;

interface AbsenceFiltersProps {
  handleFilters: (props: SetValueProps) => void;
  filters: FilterValues;
}

const AbsenceFilters: React.FC<AbsenceFiltersProps> = ({ handleFilters, filters }) => {
  const intl = useIntl();

  const rawDates = filters[ABSENCES_LIST_FILTER_FIELDS.DATE_RANGE]?.value;
  const dateRangeValue =
    Array.isArray(rawDates) && rawDates.length === 2
      ? ([moment(rawDates[0]), moment(rawDates[1])] as [moment.Moment, moment.Moment])
      : null;

  return (
    <Row gutter={[16, 16]}>
      <Col span={24}>
        <Input
          placeholder={intl.formatMessage({ id: 'general.search' })}
          allowClear
          value={String(filters[ABSENCES_LIST_FILTER_FIELDS.SEARCH]?.value ?? '')}
          onChange={e =>
            handleFilters(
              buildAbsencesListFilterSetValueProps(
                ABSENCES_LIST_FILTER_FIELDS.SEARCH,
                e.target.value,
                intl,
              ),
            )
          }
          id={ABSENCES_LIST_FILTER_FIELDS.SEARCH}
        />
      </Col>
      <Col span={24}>
        <Select
          allowClear
          className="gx-w-100"
          placeholder={intl.formatMessage({ id: 'absences.type' })}
          value={String(filters[ABSENCES_LIST_FILTER_FIELDS.TYPE]?.value ?? '') || undefined}
          onChange={(value, option) => {
            const selectedLabel =
              value && option && !Array.isArray(option) ? String(option.children ?? '') : '';
            handleFilters(
              buildAbsencesListFilterSetValueProps(
                ABSENCES_LIST_FILTER_FIELDS.TYPE,
                value ?? '',
                intl,
                selectedLabel,
              ),
            );
          }}
          showSearch
          optionFilterProp="children"
          id={ABSENCES_LIST_FILTER_FIELDS.TYPE}
        >
          {ABSENCE_TYPES.map(item => (
            <Option key={item.code} value={item.code}>
              {item.label}
            </Option>
          ))}
        </Select>
      </Col>
      <Col span={24}>
        <RangePicker
          className="gx-w-100"
          placeholder={[
            intl.formatMessage({ id: 'general.start.date' }),
            intl.formatMessage({ id: 'general.end.date' }),
          ]}
          format={DateUtil.DEFAULT_DATE_FORMAT}
          value={dateRangeValue}
          onChange={dates => {
            handleFilters(
              buildAbsencesListFilterSetValueProps(
                ABSENCES_LIST_FILTER_FIELDS.DATE_RANGE,
                dates ? [dates[0], dates[1]] : [],
                intl,
              ),
            );
          }}
          allowClear
          id={ABSENCES_LIST_FILTER_FIELDS.DATE_RANGE}
        />
      </Col>
    </Row>
  );
};

export default React.memo(AbsenceFilters);
